
import React, { useState } from "react";
import { trackConversion } from "@/lib/conversions";
import { Mail, MapPin, Building2 } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useToolsConfig } from "@/hooks/useToolsConfig";
import { toolIdByPath } from "@/config/toolsRegistry";
import { openConsentSettings } from "@/lib/consent";

/** Enkel e-postkontroll (sidfoten finns på alla sidor – zod ska inte laddas här). */
class NewsletterInputError extends Error {}

function validateNewsletterEmail(raw: string): string {
  const email = raw.trim();
  if (email.length > 255) throw new NewsletterInputError("E-post får vara max 255 tecken");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) throw new NewsletterInputError("Ogiltig e-postadress");
  return email;
}

interface FooterLink {
  label: string;
  to: string;
}

const FOOTER_COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: "Elavtal",
    links: [
      { label: "Jämför elavtal", to: "/elavtal/jamfor" },
      { label: "Byt elavtal", to: "/elavtal/byt" },
      { label: "Teckna elavtal", to: "/elavtal/teckna" },
      { label: "Månadsprisavtal", to: "/elavtal/manadspris" },
      { label: "Timprisavtal", to: "/elavtal/timpris" },
      { label: "Kvartsprisavtal", to: "/elavtal/kvartspris" },
      { label: "Rörligt pris", to: "/elavtal/rorligt" },

      { label: "Fasta elavtal", to: "/elavtal/fast" },
      { label: "Visa alla", to: "/elavtal" },
    ],
  },
  {
    title: "Elbolag",
    links: [
      { label: "Jämför elbolag", to: "/elbolag/jamfor" },
      { label: "Byt elbolag", to: "/elbolag/byt" },
      { label: "Bästa elbolaget", to: "/elbolag/basta" },
      { label: "Billigaste elbolaget", to: "/elbolag/billigaste" },
      { label: "Lista: Elhandelsbolag", to: "/elhandel" },
      { label: "Lista: Elnätsbolag", to: "/elnat" },
      { label: "Visa alla", to: "/elbolag" },
    ],
  },
  {
    title: "Elpriser",
    links: [
      { label: "Elpriser just nu", to: "/elpriser" },
      { label: "Dagens elpris", to: "/elpriser/idag" },
      { label: "Morgondagens elpris", to: "/elpriser/imorgon" },
      { label: "Historiska elpriser", to: "/elpriser/historik" },
      { label: "Elprisprognos", to: "/elpriser/prognos" },
      { label: "Visa alla", to: "/elpriser" },
    ],
  },
  {
    title: "Spotpris",
    links: [
      { label: "Dagens spotpris", to: "/spotpris/idag" },
      { label: "Morgondagens spotpris", to: "/spotpris/imorgon" },
      { label: "Elområde SE1", to: "/spotpris/elomrade-se1" },
      { label: "Elområde SE2", to: "/spotpris/elomrade-se2" },
      { label: "Elområde SE3", to: "/spotpris/elomrade-se3" },
      { label: "Elområde SE4", to: "/spotpris/elomrade-se4" },
      { label: "Visa alla", to: "/spotpris" },
    ],
  },
  {
    title: "Verktyg",
    links: [
      { label: "Elmarknad AI", to: "/ai" },
      { label: "Hitta nätägare", to: "/verktyg/hitta-natagare" },
      { label: "Hitta elområde", to: "/verktyg/hitta-elomrade" },
      { label: "Hitta elbolag", to: "/verktyg/hitta-elbolag" },
      { label: "Hitta elavtal", to: "/verktyg/hitta-elavtal" },
      { label: "Hitta anläggnings-ID", to: "/verktyg/hitta-anlaggnings-id" },
      { label: "Karta över laddstationer", to: "/laddstationer" },
      { label: "Mobilapp", to: "/verktyg/mobilapp" },
      { label: "Elpris-API", to: "/elpris-api" },
      { label: "Elprisprognos API", to: "/prognos-api" },
      { label: "Visa alla", to: "/verktyg" },
    ],
  },
  {
    title: "Jämför offerter",
    links: [
      { label: "Solceller", to: "/solceller" },
      { label: "Hembatterier", to: "/batterier" },
      { label: "Värmepumpar", to: "/varmepumpar" },
      { label: "Laddboxar", to: "/laddboxar" },
      { label: "Laddstolpar", to: "/laddstolpar" },
      { label: "Kommersiella solceller", to: "/kommersiella-solceller" },
      { label: "Kommersiella batterier", to: "/kommersiella-batterier" },
    ],
  },
  {
    title: "Kundtjänst",
    links: [
      { label: "Kontakt", to: "/kontakt" },
      { label: "Frågor & svar", to: "/fragor-svar" },
      { label: "Ordlista", to: "/ordlista" },
      { label: "Felanmälan", to: "/felanmalan" },
      { label: "Skapa ärende", to: "/skapa-arende" },
      { label: "Ånger & klagomål", to: "/anger-klagomal" },
      { label: "Lämna synpunkt", to: "/lamna-synpunkt" },
      { label: "Användarvillkor", to: "/anvandarvillkor" },
      { label: "Dataskyddspolicy", to: "/dataskyddspolicy" },
      { label: "Cookies", to: "/cookies" },
      { label: "Appvillkor", to: "/app-villkor" },
      { label: "Appens integritetspolicy", to: "/app-integritetspolicy" },
    ],
  },
  {
    title: "Elmarknad.se",
    links: [
      { label: "Om oss", to: "/om-oss" },
      { label: "Jobba hos oss", to: "/jobba-hos-oss" },
      { label: "Bli partner", to: "/bli-partner" },
      { label: "Affiliateprogram", to: "/bli-partner/affiliate" },
      { label: "Whitelabel", to: "/bli-partner/whitelabel" },
      { label: "Press", to: "/press" },
      { label: "Nyheter", to: "/nyheter" },
      { label: "Rapporter", to: "/rapporter" },
      { label: "Blogg", to: "/blogg" },
      { label: "För AI & språkmodeller", to: "/for-llms" },
    ],
  },
];

const Footer = () => {
  const { isToolEnabled } = useToolsConfig();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate email
      const validatedData = { email: validateNewsletterEmail(email) };
      
      setIsSubmitting(true);

      // Check if user is logged in
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;
      // Signed-in users can only subscribe their own verified account email
      const subscriberEmail = session?.user?.email ?? validatedData.email;

      // Insert into database
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert([{ 
          email: subscriberEmail,
          user_id: userId 
        }]);


      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation - email already exists
          toast({
            title: "Du är redan prenumerant",
            description: "Den här e-postadressen är redan registrerad för vårt nyhetsbrev.",
            variant: "default",
          });
        } else {
          throw error;
        }
      } else {
        trackConversion("newsletter_signup", { form_name: "footer" }, { email: subscriberEmail });
        toast({
          title: "Tack för din prenumeration!",
          description: "Du kommer nu få våra månatliga rapporter via e-post.",
        });
        setEmail("");
      }
    } catch (error) {
      if (error instanceof NewsletterInputError) {
        toast({
          title: "Ogiltigt format",
          description: error.message,
          variant: "destructive",
        });
      } else {
        console.error("Newsletter subscription error:", error);
        toast({
          title: "Något gick fel",
          description: "Kunde inte registrera prenumerationen. Försök igen senare.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground py-12 md:py-16">
      <div className="page-container">
        {/* Newsletter Section */}
        <div className="mb-10 pb-10 md:mb-12 md:pb-12 border-b border-primary-foreground/15">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="w-8 h-8 mx-auto mb-4 text-primary-foreground/70" strokeWidth={1.75} />
            <h2 className="heading-3 text-primary-foreground mb-2">Prenumerera på vårt nyhetsbrev</h2>
            <p className="text-sm text-primary-foreground/70 mb-6">
              Få månatliga rapporter om elpriser, marknadsanalyser och smarta spartips direkt i din inkorg.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Din e-postadress"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-card text-foreground border-transparent"
                disabled={isSubmitting}
                required
              />
              <Button 
                type="submit" 
                variant="cta"
                className="px-8"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Registrerar..." : "Prenumerera"}
              </Button>
            </form>
            <p className="text-xs text-primary-foreground/55 mt-3">
              Vi respekterar din integritet. Avregistrera dig när som helst.
            </p>
          </div>
        </div>

        {/* Link columns (speglar strukturen på elmarknad.se) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8 md:gap-8">
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-primary-foreground/55 mb-3">{col.title}</h3>
              <ul className="space-y-1 md:space-y-1.5 text-sm text-primary-foreground/80">
                {col.links
                  .filter((link) => {
                    const toolId = toolIdByPath(link.to);
                    return !toolId || isToolEnabled(toolId);
                  })
                  .map((link) => (
                  <li key={link.to + link.label}>
                    <Link to={link.to} className="block py-1.5 md:py-0.5 transition-colors hover:text-primary-foreground">
                      {link.label}
                    </Link>
                  </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact section — full-width and centered */}
        <div className="mt-12 pt-10 border-t border-primary-foreground/15 text-center">
          <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-primary-foreground/55 mb-4">Kontakta oss</h3>
          <ul className="inline-flex flex-col items-start gap-3 text-sm text-primary-foreground/80 text-left">
            <li className="flex items-start gap-2">
              <Building2 size={16} className="mt-0.5 shrink-0" />
              <span>
                Elmarknad Sverige AB
                <br />
                559255-0213
              </span>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <span>
                Östermalmstorg 5
                <br />
                114 42 Stockholm
              </span>
            </li>
            <li className="flex items-center gap-2">
              <Mail size={16} className="shrink-0" />
              <a href="mailto:info@elmarknad.se" className="break-all transition-colors hover:text-primary-foreground">
                info@elmarknad.se
              </a>
            </li>
          </ul>
        </div>

        <div className="mt-10 pt-6 border-t border-primary-foreground/15 text-center text-sm text-primary-foreground/60">
          <p className="mb-2 max-w-3xl mx-auto">
             Elmarknad.se är en jämförelsetjänst inom el och energi. Vi jämför elavtal från alla
             Sverige elbolag och förmedlar kostnadsfria offerter på solceller, batterier,
            värmepumpar och elbilsladdare.
          </p>
          <p>
            Copyright &copy; {new Date().getFullYear()} Elmarknad.se
            <span aria-hidden="true"> · </span>
            <button
              type="button"
              onClick={openConsentSettings}
              className="min-h-6 underline underline-offset-2 hover:text-primary-foreground"
            >
              Cookieinställningar
            </button>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
